import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Col, Row, Form, Button, Card } from 'react-bootstrap';
import { newReference } from '../Create-Portfolio/initialValues';
import {
  LeftSidePopupsForAdding,
  Reference,
  ReturnedPortfolio,
  ReturnedReference,
} from '../Create-Portfolio/types';
import { handleNewItemFields } from '../UserProfile/helpers';
import { API_ROUTES } from '../../config/api-routes';
import axiosInstance from '../../api/api';
import { toast } from 'react-toastify';
import { getUserFacingError } from '../../utils/userFacingError';
import { toastOnce } from '../../utils/toastOnce';
import { useSessionStore } from '../../store/sessionStore';

interface Props {
  initialReference?: Reference;
  setPopupOpen?: React.Dispatch<React.SetStateAction<LeftSidePopupsForAdding>>;
  setOpenedPopups?: React.Dispatch<React.SetStateAction<{ references: boolean[] }>>;
  setPortfolioData?: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton?: React.Dispatch<React.SetStateAction<boolean>>;
  index?: number;
  /** When true, render form only (no nested card) for use inside Bootstrap Modal */
  embedded?: boolean;
  /** Hide inline name/email subtitle when the parent modal supplies a header */
  hideEmbeddedMeta?: boolean;
  onClose?: () => void;
}

function normalizeVerificationAnswers(raw: unknown): ReturnedReference['verificationAnswers'] {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }
  return null;
}

function normalizeSavedReference(raw: unknown): ReturnedReference | null {
  if (!raw || typeof raw !== 'object') return null;
  const envelope = raw as Record<string, unknown>;
  const row = (envelope.reference ?? envelope) as Record<string, unknown>;
  if (!row.id) return null;
  return {
    id: Number(row.id),
    name: String(row.name ?? ''),
    email: String(row.email ?? ''),
    role: String(row.role ?? ''),
    organization: String(row.organization ?? row.company ?? ''),
    phone: String(row.phone ?? ''),
    isReferenceConfirmed: Boolean(row.isReferenceConfirmed ?? row.is_verified ?? row.verified_at),
    verificationAnswers: normalizeVerificationAnswers(
      row.verificationAnswers ?? row.verification_answers
    ),
    created_at: (row.created_at as string) ?? new Date().toISOString(),
    updated_at: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

const ReferencePopup: React.FC<Props> = ({
  initialReference,
  setPopupOpen,
  setOpenedPopups,
  setPortfolioData,
  setSavePortfolioButton,
  index,
  embedded = false,
  hideEmbeddedMeta = false,
  onClose,
}) => {
  const [reference, setNewReference] = useState<Reference>(initialReference || newReference);
  const [isSaving, setIsSaving] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const isEdit = !!initialReference;

  const closePopup = useCallback(() => {
    onClose?.();
    setPopupOpen?.((prev) => ({ ...prev, references: false }));
    if (setOpenedPopups && typeof index === 'number') {
      setOpenedPopups((prev) => {
        const newReferencesPopups = [...prev.references];
        newReferencesPopups[index] = false;
        return {
          ...prev,
          references: newReferencesPopups,
        };
      });
    }
  }, [setPopupOpen, setOpenedPopups, index, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        // fixed commented out closePopup to prevent closing on outside click
        // closePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupRef, setPopupOpen, closePopup]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = reference.email?.trim().toLowerCase() || '';
    const sessionUser = useSessionStore.getState().currentUser as
      | { email?: string }
      | null
      | undefined;
    const ownEmail = String(sessionUser?.email || '')
      .trim()
      .toLowerCase();

    if (email && ownEmail && email === ownEmail) {
      toast.error('You cannot add yourself as a reference.');
      return;
    }

    if (!isEdit && email) {
      const portfolio = useSessionStore.getState().portfolio as
        | { references?: Array<{ email?: string; id?: number }> }
        | null
        | undefined;
      const existing = portfolio?.references ?? [];
      const duplicate =
        Array.isArray(existing) &&
        existing.some(
          (row) =>
            String(row.email || '')
              .trim()
              .toLowerCase() === email
        );
      if (duplicate) {
        toast.error('A reference with this email already exists.');
        return;
      }
    }

    try {
      setIsSaving(true);
      const requestUrl = isEdit
        ? `${API_ROUTES.REFERENCE.UPDATE_REFERENCE}/${initialReference!.id}`
        : API_ROUTES.REFERENCE.CREATE_REFERENCE;
      const method = isEdit ? 'put' : 'post';

      const response = await axiosInstance({
        method,
        url: requestUrl,
        data: { reference },
        suppressErrorToast: true,
      });

      if (!response || response.status < 200 || response.status >= 300) {
        throw new Error('Failed to save reference');
      }

      const savedReference = normalizeSavedReference(response.data);
      if (!savedReference) {
        throw new Error('Invalid reference response');
      }

      if (setPortfolioData) {
        setPortfolioData((prev) => {
          const updatedReferences = [...(prev.references || [])];
          if (isEdit && typeof index === 'number') {
            updatedReferences[index] = savedReference;
          } else {
            updatedReferences.push(savedReference);
          }
          return {
            ...prev,
            references: updatedReferences,
          };
        });
        toastOnce(
          isEdit ? 'Reference updated successfully' : 'Verification email sent to your reference'
        );
      }
    } catch (error) {
      toast.error(
        getUserFacingError(
          error,
          'We could not save this reference. Please check the details and try again.'
        )
      );
    } finally {
      setIsSaving(false);
      closePopup();
    }
  };

  const formContent = (
    <>
      {!embedded ? (
        <Card.Title className="mb-2">{isEdit ? 'Edit reference' : 'Add reference'}</Card.Title>
      ) : null}
      {!isEdit ? (
        <p className="text-muted small mb-3">
          We email each reference a secure link to verify and submit short feedback tied to your
          profile.
        </p>
      ) : null}
      {isEdit && !hideEmbeddedMeta && (reference.name || reference.email) ? (
        <div className="text-muted small mb-3">
          {reference.name || 'Reference'}
          {reference.email ? ` • ${reference.email}` : ''}
        </div>
      ) : null}
      <Form onSubmit={handleSubmit} className={embedded ? 'ref-edit-form' : undefined}>
        <Form.Group as={Row} className="mb-3">
          <Col sm={12}>
            {embedded ? <Form.Label className="ref-edit-label">Full name</Form.Label> : null}
            <Form.Control
              type="text"
              required
              placeholder={embedded ? '' : 'Full name'}
              onChange={(e) => handleNewItemFields('name', e.target.value, setNewReference)}
              value={reference.name}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={6}>
            {embedded ? <Form.Label className="ref-edit-label">Company</Form.Label> : null}
            <Form.Control
              type="text"
              required
              placeholder={embedded ? '' : 'Company'}
              onChange={(e) => handleNewItemFields('organization', e.target.value, setNewReference)}
              value={reference.organization}
            />
          </Col>
          <Col sm={6}>
            {embedded ? <Form.Label className="ref-edit-label">Role</Form.Label> : null}
            <Form.Control
              type="text"
              required
              placeholder={embedded ? '' : 'Role'}
              onChange={(e) => handleNewItemFields('role', e.target.value, setNewReference)}
              value={reference.role}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={6}>
            {embedded ? <Form.Label className="ref-edit-label">Email</Form.Label> : null}
            <Form.Control
              type="email"
              required
              placeholder={embedded ? '' : 'Email'}
              onChange={(e) => handleNewItemFields('email', e.target.value, setNewReference)}
              value={reference.email}
            />
          </Col>
          <Col sm={6}>
            {embedded ? <Form.Label className="ref-edit-label">Phone (optional)</Form.Label> : null}
            <Form.Control
              type="tel"
              placeholder={embedded ? '' : 'Phone (optional)'}
              onChange={(e) => handleNewItemFields('phone', e.target.value, setNewReference)}
              value={reference.phone ?? ''}
            />
          </Col>
        </Form.Group>
        <div className={embedded ? 'ref-edit-form-actions' : undefined}>
          <Button variant="success" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Send verification request'}
          </Button>
        </div>
      </Form>
    </>
  );

  if (embedded) {
    return (
      <div ref={popupRef} className="reference-popup-embedded user-left-popup">
        {formContent}
      </div>
    );
  }

  return (
    <Card className="user-left-popup p-3" ref={popupRef}>
      <Card.Body className="scrollable-card">{formContent}</Card.Body>
    </Card>
  );
};

export default ReferencePopup;
